import type { MouseEventHandler, ReactNode } from "react";
import { Icon } from "@iconify/react";

type TurnstileRenderOptions = {
  sitekey: string;
  action: string;
  theme: "light";
  size: "flexible";
  "response-field": true;
  "response-field-name": string;
};

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
    };
    __moxipassTurnstileQueue?: Array<() => void>;
    moxipassTurnstileReady?: () => void;
  }
}

export type NavItem = {
  href: string;
  label: string;
  icon: string;
  active?: boolean;
};

export function AppShell({
  appName,
  nav,
  children,
  eyebrow,
  actions
}: {
  appName: string;
  nav: NavItem[];
  children: ReactNode;
  eyebrow?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="app-shell">
      <aside className="rail" aria-label={`${appName} navigation`}>
        <a className="brand-lockup" href="/">
          <span className="brand-mark">M</span>
          <span>
            <strong>{appName}</strong>
            {eyebrow ? <small>{eyebrow}</small> : null}
          </span>
        </a>
        <nav className="nav-list">
          {nav.map((item) => (
            <a className={item.active ? "nav-item active" : "nav-item"} href={item.href} key={item.href}>
              <Icon icon={item.icon} width="20" height="20" />
              <span>{item.label}</span>
            </a>
          ))}
        </nav>
        {actions ? <div className="rail-actions">{actions}</div> : null}
      </aside>
      <main className="workspace">{children}</main>
    </div>
  );
}

export function PageHeader({
  title,
  kicker,
  children
}: {
  title: string;
  kicker?: string;
  children?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {kicker ? <p className="kicker">{kicker}</p> : null}
        <h1>{title}</h1>
      </div>
      {children ? <div className="header-actions">{children}</div> : null}
    </header>
  );
}

export function ButtonLink({
  href,
  icon,
  children,
  variant = "primary"
}: {
  href: string;
  icon?: string;
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost";
}) {
  return (
    <a className={`button ${variant}`} href={href}>
      {icon ? <Icon icon={icon} width="18" height="18" /> : null}
      <span>{children}</span>
    </a>
  );
}

export function Button({
  icon,
  children,
  variant = "primary",
  type = "button",
  disabled,
  onClick
}: {
  icon?: string;
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost";
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <button className={`button ${variant}`} type={type} disabled={disabled} onClick={onClick}>
      {icon ? <Icon icon={icon} width="18" height="18" /> : null}
      <span>{children}</span>
    </button>
  );
}

export function StatusPill({ tone, children }: { tone: "green" | "amber" | "red" | "blue" | "neutral"; children: ReactNode }) {
  return <span className={`status-pill ${tone}`}>{children}</span>;
}

export function MetricStrip({
  items
}: {
  items: Array<{ label: string; value: string | number; detail?: string; tone?: "green" | "amber" | "red" | "blue" }>;
}) {
  return (
    <section className="metric-strip" aria-label="Metrics">
      {items.map((item) => (
        <div className="metric" key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          {item.detail ? <small className={item.tone ? `tone-${item.tone}` : undefined}>{item.detail}</small> : null}
        </div>
      ))}
    </section>
  );
}

export function ProgressMeter({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = Math.max(0, Math.min(100, Math.round((value / max) * 100)));
  return (
    <div className="progress-meter" aria-label={label}>
      <div className="progress-row">
        <span>{label}</span>
        <strong>
          {value}/{max}
        </strong>
      </div>
      <div className="progress-track">
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function Surface({
  title,
  icon,
  children,
  action
}: {
  title?: string;
  icon?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="surface">
      {title ? (
        <div className="surface-heading">
          <h2>
            {icon ? <Icon icon={icon} width="20" height="20" /> : null}
            <span>{title}</span>
          </h2>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function DataTable({
  columns,
  rows
}: {
  columns: string[];
  rows: Array<Array<ReactNode>>;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function EmptyState({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="empty-state">
      <Icon icon={icon} width="34" height="34" />
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

export function TurnstileChallenge({ siteKey, action }: { siteKey?: string; action: string }) {
  if (!siteKey) return null;
  const resolvedSiteKey = siteKey;

  function renderTurnstile(node: HTMLDivElement | null) {
    if (!node || node.dataset.turnstileRendered === "true") return;

    const render = () => {
      if (!window.turnstile || node.dataset.turnstileRendered === "true") return;
      window.turnstile.render(node, {
        sitekey: resolvedSiteKey,
        action,
        theme: "light",
        size: "flexible",
        "response-field": true,
        "response-field-name": "turnstileToken"
      });
      node.dataset.turnstileRendered = "true";
    };

    if (window.turnstile) {
      render();
      return;
    }

    window.__moxipassTurnstileQueue = window.__moxipassTurnstileQueue ?? [];
    window.__moxipassTurnstileQueue.push(render);
  }

  return (
    <div className="turnstile-wrap">
      <div ref={renderTurnstile} />
    </div>
  );
}
